import type * as type from "@vTypes";
import { server_url } from "@script/config";
import type { LastScheduleResponse } from "@vTypes/version-type.ts";
import type { Version } from "@/hooks/roster-craete/useRosterVersions";
import { useGroupStore } from "@/store/groupStore";
import { getSelectedGroupId } from "@/script/group_sync";
import httpClient from "@/config/axios";

const INVALID_GROUP_ID = new Set(["", "undefined", "null", "NaN"]);
function _normalizeGid(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return v && !INVALID_GROUP_ID.has(v) ? v : null;
}
function resolveGid(explicit?: string | null): string | null {
  return (
    _normalizeGid(explicit) ??
    _normalizeGid(useGroupStore.getState().selectedGroupId) ??
    _normalizeGid(getSelectedGroupId())
  );
}

export type GroupResponse = {
  group_id: string;
  group_name: string;
  office_id: string;
};

// 그룹(병동) 목록
export async function groups() {
  const response = await fetch(`${server_url}/groups`, {
    credentials: "include",
  });
  // console.log("req list_res : ", response);
  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status}`);
  }
  return (await response.json()) as GroupResponse[];
}

// 그룹 생성
export async function create_group(group_name: string) {
  const response = await fetch(`${server_url}/groups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ group_name, office_name: group_name }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `그룹 생성 실패`);
  }
  return await response.json();
}

// 그룹 수정
export async function update_group(group_id: string, group_name: string) {
  const response = await fetch(`${server_url}/groups/${group_id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ group_name }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `그룹 수정 실패`);
  }
  return await response.json();
}

export async function wanted_all(group_id?: string) {
  const base = `${server_url}/wanted/all`;
  const url =
    group_id && group_id.trim() !== "" && group_id !== "null"
      ? `${base}?group_id=${encodeURIComponent(group_id)}`
      : base;
  const response = await fetch(url, {
    credentials: "include",
  });
  if (!response.ok) {
    // throw new Error(`선호도 상태를 확인 할 수 없습니다.`);
    throw new Error(`Fetch failed: ${response.status}`);
  }
  const result: type.WantedAll[] = await response.json();
  return result;
}

export async function weeklyOff_my(
  year: number,
  month: number,
  // group_id?: string,
  nurse_id?: string
) {
  // TODO : docs에는 group_id  없어서 일단 제거 / 나중에 필요하면 추가
  // const base = `${server_url}/weekly-off/my?year=${year}&month=${month}`;
  // const hasValidGroupId = !!(
  //   group_id &&
  //   group_id.trim() !== "" &&
  //   group_id !== "null"
  // );
  // let url = hasValidGroupId
  //   ? `${base}&group_id=${encodeURIComponent(group_id!)}`
  //   : base;
  let url = `${server_url}/weekly-off/my?year=${year}&month=${month}`;

  if (nurse_id) {
    url += `&nurse_id=${encodeURIComponent(nurse_id)}`;
  }

  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    // throw new Error(`주휴 확인 할 수 없습니다.`);
    throw new Error(`Fetch failed: ${response.status}`);
  }
  return await response.json();
}

export async function holidays(year: number, month: number) {
  const response = await fetch(
    `${server_url}/dates/holidays?year=${year}&month=${month}`,
    { credentials: "include" }
  );
  if (!response.ok) {
    // throw new Error(`휴일 정보를 가져오지 못했습니다.`);
    throw new Error(`Fetch failed: ${response.status}`);
  }
  const data = await response.json();
  if (!Array.isArray(data) || !data.every((item) => typeof item === "string")) {
    // throw new Error(`휴일 정보를 가져오지 못했습니다.`);
    throw new Error(`Fetch failed: ${response.status}`);
  }
  return [...new Set(data)];
}

export async function shifts(
  group_id?: string | null
): Promise<type.res_shifts[]> {
  return httpClient.get<type.res_shifts[]>("/shifts", {
    params: {
      ...(group_id ? { group_id } : {}),
    },
  });
}

export async function roster_issued(group_id?: string) {
  const base = `${server_url}/roster/issued`;
  const url = group_id
    ? `${base}?group_id=${encodeURIComponent(group_id)}`
    : base;
  const response = await fetch(url, {
    credentials: "include",
  });
  if (!response.ok) {
    if (response.status === 404) {
      return [];
    }
    throw new Error(`Fetch failed: ${response.status}`);
  }
  const result: { year: number; month: number; schedule_id: string }[] =
    await response.json();
  return result;
}

export async function roster(year: number, month: number, group_id?: string) {
  const base = `${server_url}/roster/${year}/${month}`;
  const url = group_id
    ? `${base}?group_id=${encodeURIComponent(group_id)}`
    : base;
  const response = await fetch(url, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status}`);
  }
  // console.log("response!!", response);
  const result: type.SchedulesRawData[] = await response.json();
  return result;
}

export async function roster_latest(group_id?: string | null) {
  // 최신 월과 버전의 스케줄 정보 조회 (수간호사용)
  // year, month, version desc

  const base = `${server_url}/roster/latest`;
  const gid = resolveGid(group_id);
  const url = gid ? `${base}?group_id=${encodeURIComponent(gid)}` : base;
  const response = await fetch(url, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status}`);
  }
  return (await response.json()) as LastScheduleResponse;
}

export async function roster_versions(
  year: number,
  month: number,
  group_id?: string | null
) {
  // 특정 월의 모든 버전 목록 조회 (수간호사용)
  const base = `${server_url}/roster/${year}/${month}/versions`;
  const gid = resolveGid(group_id);
  const url = gid ? `${base}?group_id=${encodeURIComponent(gid)}` : base;
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status}`);
  }
  const result: Version[] = await response.json();
  return result;
}

export async function roster_schedule(scheduleId: string, group_id?: string) {
  // 특정 schedule_id의 근무표 조회
  const base = `${server_url}/roster/schedule/${scheduleId}`;
  const url = group_id
    ? `${base}?group_id=${encodeURIComponent(group_id)}`
    : base;
  const response = await fetch(url, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status}`);
  }
  return await response.json();
}

export async function roster_prev_tail(
  year: number,
  month: number,
  options?: {
    schedule_id?: string;
    tail_days?: number;
    group_id?: string;
  }
) {
  // 전월 말일 근무표 데이터 조회
  const base = `${server_url}/roster/${year}/${month}/prev-tail`;
  const params = new URLSearchParams();

  if (options?.schedule_id) {
    params.append("schedule_id", options.schedule_id);
  }
  if (options?.tail_days !== undefined) {
    params.append("tail_days", String(options.tail_days));
  }
  if (options?.group_id) {
    params.append("group_id", options.group_id);
  }

  const queryString = params.toString();
  const url = queryString ? `${base}?${queryString}` : base;

  const response = await fetch(url, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status}`);
  }

  // data: null이면 전월 근무표가 없음을 의미
  const result = await response.json();
  return result as type.SchedulesRawData | null;
}

export async function roster_submission(
  year: number,
  month: number,
  group_id?: string
) {
  // 해당 기간에 근무표 제출한 인원 아이디 출력
  // [{string}] | [] 출력
  const base = `${server_url}/roster/${year}/${month}/submissions`;
  const gid = resolveGid(group_id);
  const url = gid ? `${base}?group_id=${encodeURIComponent(gid)}` : base;
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status}`);
  }
  const text = await response.text();
  // 응답 본문이 비어있는 경우, 파싱을 시도하지 않고 기본값을 반환합니다.
  if (!text) {
    return { submitted_nurses: [] };
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    console.error(
      "roster_submission JSON parsing error:",
      e,
      "Raw text:",
      text
    );
    throw new Error("서버 응답을 파싱하는 데 실패했습니다.");
  }
}

export async function roster_config_version(ver: string, group_id?: string) {
  return httpClient.get(`/roster/config/version/${ver}`, {
    params: {
      ...(group_id ? { group_id } : {}),
    },
  });
}

export async function roster_config_version_all(group_id?: string) {
  return httpClient.get("/roster/config/versions", {
    params: {
      ...(group_id ? { group_id } : {}),
    },
  });
}

export async function shiftManage(ver: string, group_id?: string | null) {
  return httpClient.get("/shift-manage/RN", {
    params: {
      config_version: ver,
      ...(group_id ? { group_id } : {}),
    },
  });
}

export async function addShift(data: type.AddShift, group_id?: string) {
  return httpClient.post("/shifts/add", data, {
    params: {
      ...(group_id ? { group_id } : {}),
    },
  });
}

export async function updateShift(data: type.UpdateShift, group_id?: string) {
  return httpClient.post("/shifts/update", data, {
    params: {
      ...(group_id ? { group_id } : {}),
    },
  });
}

export async function deleteShift(shift_id: string, group_id?: string) {
  return httpClient.post(
    "/shifts/remove",
    { shift_id },
    {
      params: {
        ...(group_id ? { group_id } : {}),
      },
    }
  );
}

export async function updateShiftSequence(
  shift_id: string,
  new_sequence: number,
  group_id?: string
) {
  return httpClient.post(
    "/shifts/move",
    { shift_id, new_sequence },
    {
      params: {
        ...(group_id ? { group_id } : {}),
      },
    }
  );
}

export async function saveShiftManage(
  payload: type.ShiftManageObject[],
  group_id?: string
) {
  return httpClient.post(
    "/shift-manage/save",
    {
      class_name: "RN",
      slots: payload,
    },
    {
      params: {
        config_version: "default",
        ...(group_id ? { group_id } : {}),
      },
    }
  );
}

export async function saveRoster(
  data: {
    year: number;
    month: number;
    roster: unknown;
    schedule_id: string;
    memo: string;
  },
  group_id?: string
) {
  const base = `${server_url}/roster/save`;
  const url = group_id
    ? `${base}?group_id=${encodeURIComponent(group_id)}`
    : base;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "저장에 실패했습니다.");
  }
  return await response.json();
}
